import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '../view';
import {css} from '../css';

const TRANSITION_TIMING = '200ms';

const TRANSITION = [
  `transform ${TRANSITION_TIMING} ease-in-out`,
  `opacity ${TRANSITION_TIMING} ease-in-out`,
  `grid-template-rows ${TRANSITION_TIMING} ease-in-out`,
].join(', ');

function isEmptyValue(value: string): boolean {
  return !value || value === '0' || value === '0px';
}

export type CollapsibleContainerProps = View.IntrinsicElement<
  'section',
  {
    /**
     * Minimum height to which the panel should collapse. Used for truncating long content.
     */
    minHeight: string;
    /**
     * Spread into the component from collapsibleProps returned from useCollapsible
     */
    id: string;
    /**
     * Open state of Collapsible area. Spread into the component from collapsibleProps returned from useCollapsible
     */
    open: boolean;
    /**
     * Contents of the collapsible container.
     */
    children: React.ReactNode;
  }
>;

export const CollapsibleContainerConfig = createViewConfig({
  props: {} as CollapsibleContainerProps,
  name: 'CollapsibleContainer',
  defaults: {
    minHeight: '0',
  },
  flattens: true,
});

export const CollapsibleContainer = CollapsibleContainerConfig.createView(
  ({minHeight, open, id, children, ...props}) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const uses = React.useMemo(() => {
      const hasMinHeight = !isEmptyValue(minHeight);
      const visibleContent = open || hasMinHeight;

      return [
        css({
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          // When the collapsible has a minimum height, we cannot start the
          // animation from 0 as this would have no effect. To counteract this,
          // we add to the bottom padding to enlarge the box, and we will
          // subtract it again from the margin of the inner container.
          paddingBottom: minHeight,
          opacity: visibleContent ? '1' : '0',
          overflow: 'hidden',
          position: 'relative',
          scrollbarWidth: 'none',
          transform: visibleContent
            ? 'translateY(0) scale(1)'
            : 'translateY(-2px) scale(0.99)',
          // During the closing transition, we delay by TRANSITION_TIMING
          // switching visibility to hidden, so that the content stays visible
          // while the collapsible closes.
          transition: open
            ? `${TRANSITION}, visibility 0ms linear`
            : `${TRANSITION}, visibility 0ms linear ${TRANSITION_TIMING}`,
          visibility: visibleContent ? 'visible' : 'hidden',
          '@media (prefers-reduced-motion)': {
            transitionDuration: '0ms',
          },
        }),
        hasMinHeight && !open
          ? css({
              '::after': {
                content: "''",
                position: 'absolute',
                bottom: '-40px',
                width: '100%',
                height: '40px',
                boxShadow: 'large',
              },
            })
          : css({}),
      ];
    }, [open, minHeight]);

    return (
      <view.section role="region" id={id} ref={ref} uses={uses} {...props}>
        <view.div
          css={{
            marginBottom: `-${minHeight}`,
            minHeight: '0',
            minWidth: '0',
          }}
        >
          {children}
        </view.div>
      </view.section>
    );
  },
);
